import { useState } from 'react';
import { isStale, placementFor, resultFor, useApp } from '../../app/store';
import { ALL_METRICS, METRICS } from '../../app/metrics';
import { keyMessage } from '../../app/keyMessage';
import { fmt } from '../../app/format';
import { YearChart, yearStatus } from '../charts/YearChart';
import { ChartFrame } from '../charts/common';
import { BuildingMetaTable, KpiRow } from '../components/BuildingHead';
import { AnomalyChart, AnomalyLegend } from '../charts/AnomalyChart';
import { toneHex } from '../../app/metrics';
import { GwrPanel, HintsPanel, QualityPanel, QueueNotice } from '../components/Panels';
import { LocatorMap } from '../components/LazyMaps';
import { EmptyPortfolio } from './PortfolioView';
import { go } from '../hooks';

export function OverviewView() {
  const s = useApp();
  const [mode, setMode] = useState<'abweichung' | 'werte'>('abweichung');
  const building = s.buildings.find((b) => b.id === s.focusId);
  if (!building) return <EmptyPortfolio />;
  const placement = placementFor(s, building.id);
  const result = resultFor(s, building.id);
  const stale = isStale(s, result);
  const cfg = s.config;
  const cell = placement ? s.cells[placement.cellKey] : undefined;
  const message = keyMessage(result, cfg);
  const meta = METRICS[s.metric];
  const failure = s.placementFailures[building.id];
  const openYear = (y: number) => {
    s.setDetailYear(y);
    go('detail');
  };
  const years = result?.years ?? [];
  const anomalyOk = !!result && result.reference.status !== 'ungenuegend';
  const showAnomaly = mode === 'abweichung' && anomalyOk;

  return (
    <div>
      <header>
        <div className="eyebrow">Standortübersicht</div>
        <h1>{building.label}</h1>
        {message ? (
          <p className={`lead${stale ? ' stale' : ''}`}>{message}</p>
        ) : (
          <p className="lead muted">{failure ? `Keine Rasterzelle: ${failure}` : 'Die Daten werden geladen. Das letzte vollständige Jahr erscheint zuerst, die Referenzperiode wird schrittweise ergänzt.'}</p>
        )}
        {stale && (
          <p className="small" role="status" style={{ marginTop: 8, color: 'var(--warn)' }}>
            Neuberechnung mit geänderten Annahmen läuft – ausgegraute Werte stammen aus der vorherigen Einstellung.
          </p>
        )}
      </header>

      <div style={{ marginTop: 16 }}>
        <QueueNotice />
      </div>

      <KpiRow result={result} cfg={cfg} selected={s.metric} onSelect={s.setMetric} stale={stale} />

      <div className="grid-main">
        <div className="stack">
          <ChartFrame
            id="hauptdiagramm"
            stale={stale}
            title={
              <>
                {meta.label} je Kalenderjahr, {building.label}
              </>
            }
            sub={
              <>
                {showAnomaly ? `Abweichung vom Mittel ${cfg.referenceStart}–${cfg.referenceEnd}` : meta.definition(cfg)} · {cfg.periodStart}–{cfg.periodEnd} · {meta.unit} · Balken anklicken für das Tagesdetail
              </>
            }
            actions={
              <div className="row" style={{ gap: 8 }}>
                <div className="seg" role="group" aria-label="Darstellung">
                  <button type="button" aria-pressed={showAnomaly} disabled={!anomalyOk} onClick={() => setMode('abweichung')}>
                    Abweichung
                  </button>
                  <button type="button" aria-pressed={!showAnomaly} onClick={() => setMode('werte')}>
                    Werte
                  </button>
                </div>
                <label className="row" style={{ gap: 6 }}>
                  <span className="sr-only">Kennzahl wählen</span>
                  <select className="select" style={{ minHeight: 36, width: 'auto', fontSize: 14 }} value={s.metric} onChange={(e) => s.setMetric(e.target.value as typeof s.metric)}>
                    {ALL_METRICS.map((k) => (
                      <option key={k} value={k}>
                        {METRICS[k].label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            }
            table={<YearTable years={years} from={cfg.periodStart} to={cfg.periodEnd} metric={s.metric} onOpen={openYear} />}
          >
            {showAnomaly && result ? (
              <>
                <AnomalyChart years={years} metric={s.metric} cfg={cfg} reference={result.reference} from={cfg.periodStart} to={cfg.periodEnd} selectedYear={s.detailYear} onSelectYear={openYear} />
                <AnomalyLegend />
              </>
            ) : (
              <>
                <YearChart years={years} metric={s.metric} cfg={cfg} reference={result?.reference ?? null} from={cfg.periodStart} to={cfg.periodEnd} selectedYear={s.detailYear} onSelectYear={openYear} />
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-swatch" style={{ background: toneHex(meta.tone) }} /> vollständiges Jahr
                  </span>
                  <span className="legend-item">
                    <span className="legend-swatch" style={{ background: 'repeating-linear-gradient(45deg,#9ca3af 0 1.5px,#fff 1.5px 4px)', border: '1px solid #9ca3af' }} /> Teilwert (Nenner im Tooltip)
                  </span>
                  <span className="legend-item">
                    <span className="legend-swatch" style={{ background: '#d1d5db', height: 3 }} /> noch nicht geladen
                  </span>
                  <span className="legend-item">
                    <svg width="22" height="10" aria-hidden="true">
                      <line x1="0" x2="22" y1="5" y2="5" stroke="#1f2937" strokeWidth="1.5" strokeDasharray="6 4" />
                    </svg>
                    Referenzmittel
                  </span>
                </div>
              </>
            )}
          </ChartFrame>
          <HintsPanel result={result} cfg={cfg} />
        </div>
        <aside className="stack aside-grid" aria-label="Lage, Datenqualität und Gebäude">
          <div>
            <h2 style={{ fontSize: 18, marginBottom: 8 }}>Standort</h2>
            <BuildingMetaTable building={building} placement={placement} cellElevation={cell?.elevation ?? null} />
            <div style={{ marginTop: 16 }}>
              <LocatorMap building={building} placement={placement} />
            </div>
          </div>
          <QualityPanel result={result} placement={placement} cell={cell} cfg={cfg} building={building} />
          <GwrPanel building={building} />
        </aside>
      </div>
    </div>
  );
}

export function YearTable({ years, from, to, metric, onOpen }: { years: import('../../core/domain/types').YearMetrics[]; from: number; to: number; metric: import('../../core/domain/types').MetricKey; onOpen?: (y: number) => void }) {
  const meta = METRICS[metric];
  const rows = [];
  for (let y = to; y >= from; y--) rows.push(years.find((m) => m.year === y));
  return (
    <div className="table-wrap" tabIndex={0} style={{ maxHeight: 420 }} role="region" aria-label={`Tabelle ${meta.label}`}>
      <table className="data">
        <thead>
          <tr>
            <th>Jahr</th>
            <th className="n">
              {meta.label} [{meta.unit}]
            </th>
            <th>Status</th>
            <th className="n">Gültige Tage</th>
            <th className="n">Gültige Nächte</th>
            <th className="n">Fehlende Stunden</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => {
            const year = to - i;
            const st = yearStatus(m);
            return (
              <tr key={year}>
                <td>{onOpen ? <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0 6px', minHeight: 24 }} onClick={() => onOpen(year)}>{year}</button> : year}</td>
                <td className="n">{m && (st === 'vollständig' || st === 'Teilwert') ? fmt(m[metric] as number, meta.decimals) : '–'}</td>
                <td>{st}</td>
                <td className="n">{m ? `${m.daysValid} / ${m.daysExpected}` : '–'}</td>
                <td className="n">{m ? `${m.nightsValid} / ${m.nightsExpected}` : '–'}</td>
                <td className="n">{m ? fmt(m.hoursMissing) : '–'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
